import { useContext, useRef } from 'react';
import { Button } from '../../../button/Button';
import { IconButton } from '../../../button/IconButton/IconButton';
import { Icon } from '../../../icon/Icon';
import { ListConductorRawFilter } from '../../../list-conductor/filters';
import { FullScreenModal } from '../../../modal/FullScreenModal/FullScreenModal';
import { IntegrationsFactory } from '../../../pipelines/factories/integrationFactory';
import { useInMemoryFilteredList } from '../../../shared/hooks/useInMemoryFilteredList';
import { getDataIdGenerator } from '../../../shared/utils/dataId';
import { matchFilters } from '../../../shared/utils/filters';
import { getFilterKeyMatchValue, getSortValue } from '../Utils';
import { PipelineSubscriptionsList } from './PipelineSubscriptionsList';
import { AlertsAPIservice } from '../../APIServices/APIServiceContext';

export interface AddPipelineSubscriptionListProps {
  open: boolean;
  onClose: (D: boolean) => void;
}
const dataIdGenerator = getDataIdGenerator('add_pipeline_subscription_list');
export function AddPipelineSubscriptionList({ open, onClose }: AddPipelineSubscriptionListProps) {
  const entityListRef = useRef<{ fetch: () => void } | null>(null);
  const { APIService } = useContext(AlertsAPIservice);

  const {
    fetch,
    selectedItems,
    reset,
    onAllInPageSelectionChange,
    onAllItemsSelectionChange,
    onItemSelectionChange,
    onBulkActionCTAClick,
    resetSelectedItems
  } = useInMemoryFilteredList(
    async () => {
      return APIService.getSubscriptions().then(([pipelines, subscribedPipelines]) => {
        const subscribedPipelineIds = new Set(
          subscribedPipelines.data.subscriptions.map(data => data.entity_id)
        );
        const subscribedPipelinesData = pipelines.data.filter(
          pipeline => !subscribedPipelineIds.has(pipeline.id.toString())
        );
        return IntegrationsFactory(subscribedPipelinesData);
      });
    },
    (item, params) => {
      return matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterKeyMatchValue(filterKey, item)
      );
    },
    undefined,
    undefined,
    (item, field) => getSortValue(item, field) ?? '',
    false,
    'id',
    undefined,
    async items => {
      await APIService.subscribePipelines(items);
      resetSelectedItems();
      onClose(true);
    }
  );

  return (
    <FullScreenModal open={open}>
      <div className='h-100'>
        <div className='d-flex justify-between px-5 pt-5'>
          <div>
            <div className='text-subheading-2'>Add Pipeline Subscriptions</div>

            <div className='text-body-1 text-secondary mt-1'>
              Select the Pipelines for which you wish to receive alerts
            </div>
          </div>

          <div>
            <IconButton
              dataId={dataIdGenerator('close')}
              label='Close'
              onClick={() => {
                onClose(false);
              }}
            >
              <Icon name='close' size={4} />
            </IconButton>
          </div>
        </div>

        <div className='main-content-area' style={{ maxHeight: 'calc(100% - 116px)' }}>
          <PipelineSubscriptionsList
            innerRef={entityListRef}
            selectedItems={selectedItems}
            entity='add-pipeline'
            onAllInPageSelectionChange={onAllInPageSelectionChange}
            onAllItemsSelectionChange={onAllItemsSelectionChange}
            onItemSelectionChange={onItemSelectionChange}
            newListConnectedTitle='You have subscribed to all Pipelines'
            listIcon='pipeline'
            fetch={fetch}
            onRefresh={() => {
              reset();
              resetSelectedItems();
            }}
          />
        </div>

        <div className='center-flex-row justify-end px-5 pb-5'>
          <Button
            dataId={dataIdGenerator('cancel')}
            onClick={() => onClose(false)}
            icon='close'
            variation='outline'
          >
            Cancel
          </Button>

          <Button
            dataId={dataIdGenerator('subscribe')}
            onClick={() => {
              onBulkActionCTAClick();
            }}
            className=' ml-3'
            disabled={!selectedItems.size}
            icon='plus'
          >
            add pipelines subscription
          </Button>
        </div>
      </div>
    </FullScreenModal>
  );
}
